"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { CalendarDays, Clock, Globe, Package, type LucideIcon } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary, usePartialPaymentOrders } from "@/features/dashboard/hooks";
import {
  PartialPaymentBanner,
  PartialPaymentBannerSkeleton,
} from "@/features/dashboard/components/PartialPaymentBanner";
import {
  findPartialOrderById,
  findPartialOrderForCourse,
} from "@/features/dashboard/partial-payment";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type Course,
  type CoursePrice,
} from "../api";
import type { Locale } from "@/lib/i18n";
import { useCoursePricing } from "./CoursePricingProvider";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { CohortWaitlistForm } from "./CohortWaitlistForm";
import { RefundLineIcon } from "./detail/RefundLineIcon";
import { CourseCohortLine } from "./CourseCohortLine";
import { cohortStatus } from "../lib/cohort-line";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  formatTierUpgradeCtaLabel,
  isPlanIdActiveUpgradeTarget,
  resolvePlanPricingUiKind,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { scrollToCoursePricingComparison } from "../lib/scroll-course-pricing";
import { getSeatIndicator } from "../lib/course-pricing-helpers";
import { signInHref } from "@/lib/post-login-redirect";

function scrollToCoursePricing(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  scrollToCoursePricingComparison();
}

/** The phone's first-screen purchase card; `CourseMobilePricingBar` shows once it has scrolled away. */
export const HERO_BUY_ID = "course-hero-buy";

export type CourseHeroPurchaseRowProps = {
  locale: Locale;
  /** Preformatted duration, e.g. `12h 30m`, or null to omit */
  durationLine: string | null;
  /** Raw language label from API, or null */
  languageLine: string | null;
  /** Preformatted project count line from server i18n, or null */
  projectsLine: string | null;
  children?: ReactNode;
  showPurchaseUi: boolean;
  courseId: string;
  slug: string;
  fallbackPrice: CoursePrice;
  eligibility?: Course["eligibility"];
  firstLessonId: string | null;
  /** The course's own refund line (`refundPolicyText_*`), localized; left out when empty. */
  refundNote?: string | null;
  /** The course's earliest cohort, used when it is sold without plans. */
  nextCohortStartAt?: string | null;
};

export function CourseHeroPurchaseRow({
  locale,
  durationLine,
  languageLine,
  projectsLine,
  children,
  showPurchaseUi,
  courseId,
  slug,
  fallbackPrice,
  eligibility,
  firstLessonId,
  refundNote,
  nextCohortStartAt,
}: CourseHeroPurchaseRowProps) {
  const t = useTranslations("courseDetail");
  const format = useFormatter();
  const { plans, selectedPlan, recommendedPlan, setSelectedPlanId, mode } = useCoursePricing();
  const { isAuthenticated, getToken } = useAuth();
  const token = getToken();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityPassed =
    hasEligibilityGate && isAuthenticated && eligibilityStatus?.passed === true;
  const eligibilityLocked = hasEligibilityGate && !eligibilityPassed;
  const eligibilityQuizSlug =
    eligibilityStatus?.quiz?.slug ?? eligibility?.quizSlug ?? "";
  const eligibilityAttemptHref = `/${locale}/quiz-exams/${eligibilityQuizSlug}/attempt`;
  const eligibilityCtaHref = isAuthenticated
    ? eligibilityAttemptHref
    : signInHref(locale, eligibilityAttemptHref);
  const eligibilityLockedReason = t("eligibilityTierLocked");
  const { data: library } = useLibrary(token ?? undefined);
  const { data: partialOrders, isLoading: partialOrdersLoading } =
    usePartialPaymentOrders(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const partialOrder =
    findPartialOrderById(partialOrders, ownedCourse?.orderId) ??
    findPartialOrderForCourse(partialOrders, courseId);
  const isOwned = !!ownedCourse;
  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  const selectedIsUpgradeTarget =
    !!selectedPlan && isPlanIdActiveUpgradeTarget(selectedPlan._id, pricingUiModel);

  const selectedPlanOwnedState =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePricingUiOwnedState(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const nominalLegacyName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const ownedTierChipName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  const heroPeerNotice =
    selectedPlan && selectedPlanOwnedState
      ? selectedPlanOwnedState.kind === "owned-other" && !selectedPlanOwnedState.upgradeable
        ? t("planOwnership.coveredByPlan", {
            planName: ownedTierChipName ?? t("planOwnership.yourPlanShort"),
          })
        : selectedPlanOwnedState.kind === "owned-legacy-peer" && nominalLegacyName
          ? t("planOwnership.enrolledInPlanChip", { planName: nominalLegacyName })
          : undefined
      : undefined;

  const heroUpgradePathBlocked =
    !!isAuthenticated &&
    !!isOwned &&
    !!selectedPlan &&
    resolvePlanPricingUiKind(selectedPlan, pricingUiModel, {
      isAuthenticated,
      isOwned,
    }) === "LOWER_OR_SIDEWAYS";

  const heroPlanName =
    getLocalizedField(
      selectedPlan?.name_en,
      selectedPlan?.name_bn,
      locale,
    ) ??
    selectedPlan?.name_en ??
    "";

  const copy = getOwnershipCopy(locale);
  const heroUpgradeUnavailableReason = heroPeerNotice
    ? undefined
    : heroUpgradePathBlocked
      ? "This tier is not offered as an upgrade from your current access. You can continue learning on your included plan or pick an upgrade tier listed for your level."
      : undefined;
  const heroUpgradeUnavailableLabel = heroPeerNotice
    ? undefined
    : heroUpgradePathBlocked
      ? `${copy.upgradePrefix} ${heroPlanName}`
      : undefined;

  // `SITE-CRS01`: the meta row names a cohort only while it is still ahead; a
  // date that has passed was printed as "Next cohort" (Full-Stack, 2026-10-05).
  let cohortLine: string | null = null;
  const cohortSource = selectedPlan ?? recommendedPlan;
  const cohortIso = cohortSource?.cohortStartAt ?? (mode === "legacy" ? nextCohortStartAt : null);
  if (mode !== "loading" && cohortIso && cohortStatus(cohortIso)?.kind === "upcoming") {
    cohortLine = t("nextCohort", {
      date: format.dateTime(new Date(cohortIso), { dateStyle: "long", timeZone: "Asia/Dhaka" }),
    });
  }

  const metaSegments: { key: string; icon: LucideIcon; node: ReactNode }[] = [];
  if (cohortLine) {
    metaSegments.push({
      key: "cohort",
      icon: CalendarDays,
      node: cohortLine,
    });
  }
  if (durationLine) {
    metaSegments.push({
      key: "duration",
      icon: Clock,
      node: durationLine,
    });
  }
  if (projectsLine) {
    metaSegments.push({
      key: "projects",
      icon: Package,
      node: projectsLine,
    });
  }
  if (languageLine) {
    metaSegments.push({
      key: "lang",
      icon: Globe,
      node: languageLine,
    });
  }

  const refundText = refundNote?.trim();
  const refundLine = refundText ? (
    <p className="flex items-start gap-2 text-[13px] leading-snug text-neutral-600">
      <RefundLineIcon />
      <span>{refundText}</span>
    </p>
  ) : null;

  /**
   * `SITE-CRS01`: the phone's first screen sells the course (price, plan, Enroll)
   * right under the title; on a desktop the sidebar card does. Its id is what the
   * phone's sticky bar watches: the bar shows once this card has scrolled away.
   */
  const renderPurchaseBlock = () => {
    if (!showPurchaseUi) return null;

    const shell = (body: ReactNode) => (
      <div
        id={HERO_BUY_ID}
        className="mt-6 space-y-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_8px_30px_rgba(0,0,0,0.06)] lg:hidden"
      >
        {partialOrdersLoading && <PartialPaymentBannerSkeleton />}
        {partialOrder && <PartialPaymentBanner order={partialOrder} locale={locale} />}
        {body}
        {refundLine}
      </div>
    );

    if (mode === "loading") {
      return shell(<Skeleton className="h-[52px] w-full rounded-[10px]" />);
    }

    if (eligibilityLocked) {
      return shell(
        <Button className="h-[52px] w-full rounded-[10px] text-[16px] font-semibold" asChild>
          <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
        </Button>,
      );
    }

    if (mode === "legacy") {
      return shell(
        <>
          <p className="text-[24px] font-bold tabular-nums text-neutral-950">
            {heroAmount(fallbackPrice, t("sidebarPlanPriceFree"))}
          </p>
          <CourseCohortLine iso={nextCohortStartAt} />
          <CourseEnrollCta
            courseId={courseId}
            slug={slug}
            price={fallbackPrice}
            locale={locale}
            firstLessonId={firstLessonId}
            enrollLabel={fallbackPrice.isFree ? undefined : t("enrollNow")}
            className="w-full"
          />
        </>,
      );
    }

    if (!selectedPlan) return null;

    const seatIndicator = getSeatIndicator(selectedPlan, {
      full: t("seats.full"),
      limited: t("seats.limited"),
      onlyLeft: (count) => t("seats.onlyLeft", { count }),
    });
    const seatFullForBuyer = seatIndicator?.full === true && !isOwned;
    const upgradeLabel = selectedIsUpgradeTarget
      ? formatTierUpgradeCtaLabel(selectedPlan, pricingUiModel, {
          upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
          upgradeFree: () => t("planOwnership.upgradeFree"),
        })
      : undefined;
    const upgradeToPlanId = selectedIsUpgradeTarget ? selectedPlan._id : undefined;

    return shell(
      <>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[24px] font-bold tabular-nums text-neutral-950">
            {heroAmount(selectedPlan.price, t("sidebarPlanPriceFree"))}
          </p>
          {seatIndicator ? (
            <span className="text-[12px] font-semibold text-primary">{seatIndicator.label}</span>
          ) : null}
        </div>
        {mode === "multi" ? (
          <div className="grid grid-cols-2 gap-2" role="group" aria-label={t("sidebarPlanPickerAria")}>
            {plans.map((plan) => {
              const name = getLocalizedField(plan.name_en, plan.name_bn, locale) ?? plan.name_en;
              return (
                <Button
                  key={plan._id}
                  type="button"
                  variant="choice"
                  size="option"
                  aria-pressed={selectedPlan._id === plan._id}
                  onClick={() => setSelectedPlanId(plan._id)}
                  className="flex-col items-start justify-center gap-0.5"
                >
                  <span className="text-[13px] font-semibold leading-snug">{name}</span>
                  {recommendedPlan?._id === plan._id ? (
                    <span className="text-[11px] font-medium text-primary">
                      {t("sidebarRecommendedShort")}
                    </span>
                  ) : null}
                </Button>
              );
            })}
          </div>
        ) : null}
        <CourseCohortLine iso={selectedPlan.cohortStartAt} />
        {seatFullForBuyer ? (
          <CohortWaitlistForm
            courseSlug={slug}
            planSlug={selectedPlan.slug ?? selectedPlan._id}
            className="w-full"
          />
        ) : (
          <CourseEnrollCta
            courseId={courseId}
            slug={slug}
            price={selectedPlan.price}
            pricingPlanId={selectedPlan._id}
            enrollLabel={selectedPlan.price.isFree ? undefined : t("enrollNow")}
            upgradeToPlanId={upgradeToPlanId}
            upgradeLabel={upgradeLabel}
            upgradeUnavailableReason={heroUpgradeUnavailableReason}
            upgradeUnavailableLabel={heroUpgradeUnavailableLabel}
            enrolledPeerNotice={heroPeerNotice}
            locale={locale}
            firstLessonId={firstLessonId}
            className="w-full"
          />
        )}
        {mode === "multi" ? (
          <Link
            href="#course-pricing-comparison"
            onClick={scrollToCoursePricing}
            className="inline-flex text-[13px] font-semibold text-primary-ink underline-offset-4 hover:underline"
          >
            {t("comparePlansLink")}
          </Link>
        ) : null}
      </>,
    );
  };

  return (
    <div>
      {metaSegments.length > 0 && (
        <div
          className={cn(
            "flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px] text-neutral-500",
            children ? "mb-4" : showPurchaseUi ? "mb-2" : "",
          )}
        >
          {metaSegments.map(({ key, icon: Icon, node }) => (
            <span key={key} className="flex items-center gap-1.5">
              <Icon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
              {node}
            </span>
          ))}
        </div>
      )}

      {renderPurchaseBlock()}

      {children ? <div className={cn(showPurchaseUi && "mt-5 lg:mt-0")}>{children}</div> : null}
    </div>
  );
}

function heroAmount(price: CoursePrice, freeLabel: string): string {
  if (price.isFree) return freeLabel;
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const amount = sale !== undefined && Number.isFinite(sale) && sale < list ? sale : list;
  return `${price.currency === "BDT" ? "৳" : "$"}${formatCourseMoneyEnIn(amount)}`;
}
